import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';

/**
 * Minimal data-fetching hook. Deliberately not a cache library — the app is
 * small enough that explicit refetching is clearer than invalidation rules.
 */
export function useApi<T>(
  path: string | null,
  query?: Record<string, string | number | boolean | null | undefined>,
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));

  // Serialised so the effect re-runs on value changes, not object identity.
  const queryKey = JSON.stringify(query ?? {});
  // Guards against a slow earlier response overwriting a newer one.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<T>(path, JSON.parse(queryKey));
      if (id === requestId.current) setData(result);
    } catch (err) {
      if (id === requestId.current) {
        setError(err instanceof ApiError ? err.message : 'Something went wrong');
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [path, queryKey]);

  useEffect(() => { void load(); }, [load]);

  return { data, error, loading, reload: load, setData };
}

/** Wraps a mutation with loading state and a consistent error message. */
export function useMutation<TArgs extends any[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (...args: TArgs): Promise<TResult | null> => {
    setLoading(true);
    setError(null);
    try {
      return await fn(...args);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong');
      return null;
    } finally {
      setLoading(false);
    }
  }, [fn]);

  return { run, loading, error, setError };
}
